import React, { useState, useEffect, useCallback } from 'react';
import { getRequestOptions } from '../helpers/Fetchwrapper';
import { KTSVG } from '../../_metronic/helpers';
import { Col, Row } from 'react-bootstrap-v5';
import 'react-dropzone-uploader/dist/styles.css'
import Select from 'react-select';
import { Link } from 'react-router-dom'
import { numberFormat } from '../helpers/GlobalHelper';
import DataTable from '../components/DataTable';

const InventoryStockStatus: React.FC = () => {

    const [selectProjectOptions, setSelectProjectOptions] = useState([])
    const [selectedProject, setSelectedProject] = useState<{ value: string; label: string; } | null>(null);

    const [warehouses, setWarehouses] = useState([]);
	const [warehouse, setWarehouse] = useState({ label: 'Select Warehouse', value: '' });

    const [itemCategories, setItemCategories] = useState<Array<{label: string, value: string}>>([]);
    const [selectedItemCategory, setSelectedItemCategory] = useState({ label: 'All Item Categories', value: '' });

    const [productCategories, setProductCategories] = useState<Array<{label: string, value: string}>>([]);
    const [selectedProductCategory, setSelectedProductCategory] = useState({ label: 'All Product Categories', value: '' });

    const [showZeroStock, setShowZeroStock] = useState(true);
    const [tableRefreshKey, setTableRefreshKey] = useState({ id: Date.now() });

    const reloadTables = () => setTableRefreshKey({ id: Date.now() });

    const projectValue = selectedProject?.value ?? '';
    const warehouseValue = warehouse?.value ?? '';

    const itemAdditionalPostData = {
        id_projects: projectValue,
        id_warehouses: warehouseValue,
        id_item_category: selectedItemCategory?.value ?? '',
        show_zero_stock: showZeroStock,
    };

    const productAdditionalPostData = {
        id_projects: projectValue,
        id_warehouses: warehouseValue,
        id_product_category: selectedProductCategory?.value ?? '',
        show_zero_stock: showZeroStock,
    };

    const getProjectList = async () => {
        const url = process.env.REACT_APP_API_URL + 'projects';

        const response = await fetch(url, getRequestOptions());

        const data = await response.json();

        data?.data?.map((element: any) => {
            element.value = element.id_projects;
            element.label = element.project_name;
            return element;
        })

        setSelectProjectOptions(data.data);
    }

    const getItemCategories = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + 'allItemCategories', getRequestOptions());
            const data = await response.json();
            
            if (data && Array.isArray(data)) {
                const categoryOptions = data.map((item: any) => ({
                    label: item.category_name,
                    value: item.id_item_categories
                }));
                
                const allOption = { label: 'All Item Categories', value: '' };
                setItemCategories([allOption, ...categoryOptions]);
            }
        } catch (error) {
            console.error('Error fetching item categories:', error);
        }
    }, []);

    const getProductCategories = useCallback(async () => {
        try {
            const response = await fetch(process.env.REACT_APP_API_URL + 'product-categories?id_projects='+ projectValue, getRequestOptions());
            const data = await response.json();
            
            if (data && Array.isArray(data)) {
                const categoryOptions = data.map((item: any) => ({
                    label: item.category_name,
                    value: item.id_product_categories
                }));
                
                const allOption = { label: 'All Product Categories', value: '' };
                setProductCategories([allOption, ...categoryOptions]);
            }
        } catch (error) {
            console.error('Error fetching product categories:', error);
        }
    }, [projectValue]);

    useEffect(() => {
        getProjectList();
        getItemCategories();
    }, [getItemCategories])

    useEffect(() => {
        getProductCategories();
    }, [getProductCategories])

    const getWarehousesByProject = (idProjects: number) => {
		if (!idProjects) {
			setWarehouses([]);
			setWarehouse({ label: 'Select Warehouse', value: '' });
			return;
		}
		fetch(
			process.env.REACT_APP_API_URL + 'warehouse/' + idProjects + '/list',
			getRequestOptions()
		)
			.then((resp) => resp.json())
			.then((resp) => {
				if (resp.success && Array.isArray(resp.data)) {
					const whOptions = resp.data.map((item: any) => ({
						label: item.name,
						value: item.id_warehouses
					}));
					setWarehouses(whOptions);
				} else {
					setWarehouses([]);
				}
				setWarehouse({ label: 'Select Warehouse', value: '' });
			})
			.catch(() => {
				setWarehouses([]);
				setWarehouse({ label: 'Select Warehouse', value: '' });
			});
	};

    const itemColumns = [
        {
            value: 'itemName',
            title: 'Item Name',
            style: { textAlign: 'left' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <Link
                    to={`/item-stock-details?id_items=${row.id_items}&id_projects=${projectValue}&id_warehouses=${warehouseValue}`}
                    className="label label-info"
                >
                    {row.itemName}
                    {row.alternateName ? (
                        <>
                            <br />
                            <span style={{ fontSize: '10px' }}>{row.alternateName}</span>
                        </>
                    ) : null}
                </Link>
            ),
        },
        { value: 'similar_item_names', title: 'Similar Item', style: { textAlign: 'left' } },
        { value: 'itemCode', title: 'Item Code', style: { textAlign: 'left' } },
        { value: 'categoryName', title: 'Item Category', style: { textAlign: 'left' } },
        {
            value: 'stock',
            title: 'Stock',
            style: { textAlign: 'right' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <span className={Number(row.stock) <= 0 ? 'text-danger' : ''}>
                    {row.stock ? numberFormat(Number(row.stock)) : 0}
                </span>
            ),
        },
        {
            value: 'average_rate',
            title: 'Average Rate',
            style: { textAlign: 'right' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => numberFormat(row.average_rate),
        },
        {
            value: 'latest_purchase_rate',
            title: 'Latest Rate',
            style: { textAlign: 'right' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => numberFormat(row.latest_purchase_rate),
        },
        { value: 'unitName', title: 'Unit', style: { textAlign: 'left' } },
    ];

    const productColumns = [
        {
            value: 'productName',
            title: 'Product Name',
            style: { textAlign: 'left' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <Link
                    to={`/finished-goods-stock-details?id_products=${row.id_products}&id_projects=${projectValue}&id_warehouses=${warehouseValue}`}
                    className="label label-info"
                >
                    {row.productName}
                    {row.alternateName ? (
                        <>
                            <br />
                            <span style={{ fontSize: '10px' }}>{row.alternateName}</span>
                        </>
                    ) : null}
                </Link>
            ),
        },
        { value: 'packing', title: 'Packing', style: { textAlign: 'left' } },
        { value: 'size', title: 'Size', style: { textAlign: 'left' } },
        { value: 'categoryName', title: 'Category', style: { textAlign: 'left' } },
        {
            value: 'stock',
            title: 'Stock',
            style: { textAlign: 'right' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => (
                <span className={Number(row.stock) <= 0 ? 'text-danger' : ''}>
                    {row.stock ? numberFormat(Number(row.stock)) : 0}
                </span>
            ),
        },
        {
            value: 'average_rate',
            title: 'Average Rate',
            style: { textAlign: 'right' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => numberFormat(row.average_rate),
        },
        {
            value: 'latest_production_rate',
            title: 'Latest Rate',
            style: { textAlign: 'right' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => numberFormat(row.latest_production_rate),
        },
        { value: 'unitName', title: 'Unit', style: { textAlign: 'left' } },
    ];

    const itemFilters = itemColumns.reduce((acc: Record<string, { filterType: string; filterValue: string }>, column) => {
        acc[column.value] = { filterType: 'like', filterValue: '' };
        return acc;
    }, {});

    const productFilters = productColumns.reduce((acc: Record<string, { filterType: string; filterValue: string }>, column) => {
        acc[column.value] = { filterType: 'like', filterValue: '' };
        return acc;
    }, {});

    const itemTableUrl = process.env.REACT_APP_API_URL + 'inventory-stock-status/items/dt';
    const productTableUrl = process.env.REACT_APP_API_URL + 'inventory-stock-status/products/dt';

    const handleSelectProjectChange = (selectedOption: any) => {
        setSelectedProject(selectedOption);
        getWarehousesByProject(selectedOption?.value);
        setWarehouse({ label: 'Select Warehouse', value: '' });
        reloadTables();
    };

    const handleWarehouseChange = (selected: any) => {
		setWarehouse(selected);
        reloadTables();
	};

    const handleItemCategoryChange = (selected: any) => {
        setSelectedItemCategory(selected);
        reloadTables();
    };

    const handleProductCategoryChange = (selected: any) => {
        setSelectedProductCategory(selected);
        reloadTables();
    };

    const handleShowZeroStockChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowZeroStock(event.target.checked);
        reloadTables();
    };

    const handleReset = () => {
        setSelectedItemCategory({ label: 'All Item Categories', value: '' });
        setSelectedProductCategory({ label: 'All Product Categories', value: '' });
        setSelectedProject(null);
        setWarehouse({ label: 'Select Warehouse', value: '' });
        setWarehouses([]);
        setShowZeroStock(true);
        reloadTables();
    };

    return (
        <>
            <div className='card card-custom'>
                <div className='card-header'>
                    <div className="card-title">
                        <KTSVG path='/media/svg/icons/Text/Bullet-list.svg' className='svg-icon-1 svg-icon-primary' />
                        <span className='text fw-bolder' style={{ marginLeft: '10px' }}>
                            Inventory Stock Status
                        </span>
                    </div>
                    <div className="card-toolbar">
                        <div className='d-flex justify-content-end align-items-center' style={{ gap: '12px', flexWrap: 'wrap' }}>
                            <div className="form-check d-flex align-items-center" style={{ minWidth: '140px' }}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showZeroStock"
                                    checked={showZeroStock}
                                    onChange={handleShowZeroStockChange}
                                />
                                <label className="form-check-label ms-2" htmlFor="showZeroStock" style={{ fontSize: '14px' }}>
                                    Show Zero Stock
                                </label>
                            </div>
                            
                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={selectedItemCategory}
                                    onChange={handleItemCategoryChange}
                                    options={itemCategories}
                                    placeholder="Select Item Category"
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                />
                            </div>

                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={selectedProductCategory}
                                    onChange={handleProductCategoryChange}
                                    options={productCategories}
                                    placeholder="Select Product Category"
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                />
                            </div>

                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={selectedProject}
                                    onChange={handleSelectProjectChange}
                                    options={selectProjectOptions}
                                    placeholder="Select project"
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                />
                            </div>

                            <div style={{ minWidth: '180px' }}>
                                <Select
                                    value={warehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouses}
                                    menuPosition="fixed"
                                    styles={{ 
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        control: base => ({ ...base, fontSize: '14px' })
                                    }}
                                    isDisabled={warehouses.length === 0}
                                />
                            </div>

                            <div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleReset}
                                    title="Reset all filters"
                                    style={{ height: '31px' }}
                                >
                                    <i className='fas fa-undo'></i> Reset
                                </button>
                            </div>

                            <div>
                                <a 
                                    className="btn btn-sm btn-primary" 
                                    href={`${process.env.REACT_APP_BACKEND}inventory_stock_status_download?id_projects=${projectValue}&id_warehouses=${warehouseValue}&id_item_category=${selectedItemCategory?.value ?? ''}&id_product_category=${selectedProductCategory?.value ?? ''}&search=`}
                                    style={{ height: '31px', display: 'inline-flex', alignItems: 'center' }}
                                >
                                    <i className='fas fa-download'></i> Excel Download
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='card-body'>
                    <Row>
                        <Col md={12} className="mb-8">
                            <h4>Item Stock</h4>
                            <DataTable
                                url={itemTableUrl}
                                columns={itemColumns}
                                filters={itemFilters}
                                newData={tableRefreshKey}
                                additionalPostData={itemAdditionalPostData}
                                defaultSortedColumn="categoryName"
                                defaultSortOrder="ASC"
                            />
                        </Col>

                        <Col md={12}>
                            <h4>Finished Goods Stock</h4>
                            <DataTable
                                url={productTableUrl}
                                columns={productColumns}
                                filters={productFilters}
                                newData={tableRefreshKey}
                                additionalPostData={productAdditionalPostData}
                                defaultSortedColumn="categoryName"
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    )
}

export default InventoryStockStatus
